import React, { FC, useMemo, useCallback, useRef, useState, useEffect } from 'react'
import { Center, useRadio, Spinner, Text, Flex } from '@chakra-ui/react'
import { Box, Image } from '../index'
import { RadioButtonProps } from './interfaces'
import { logoImageMap } from '../../state/interfaces'
import { RadioButton } from './radio-button'

export const MixedLargePillRadioButtonDecision: FC<RadioButtonProps> = (props) => {
  const { getInputProps, getCheckboxProps } = useRadio(props)
  const inputProps = getInputProps()
  const image = useMemo(() => (props.image ? logoImageMap[props.image] : ''), [props])
  const imageSelected = useMemo(() => (props.imageSelected ? logoImageMap[props.imageSelected] : ''), [props])
  const inputRef = useRef<HTMLInputElement>(null)
  const [isHovered, setIsHovered] = useState(false)
  const [isHoverImageLoaded, setIsHoverImageLoaded] = useState(false)
  const [isClicked, setIsClicked] = useState(false)

  // preloading image
  useEffect(() => {
    const link = document.createElement('link')
    link.rel = 'preload'
    link.as = 'image'
    link.href = imageSelected
    document.head.appendChild(link)

    // Clean up function
    return () => {
      document.head.removeChild(link)
    }
  }, [imageSelected])

  const handleMouseEnter = useCallback(() => {
    if (isClicked) {
      return
    }
    setIsHovered(true)
  }, [])

  const handleMouseLeave = useCallback(() => {
    if (isClicked) {
      return
    }
    setIsHovered(false)
  }, [])

  const handleFocus = () => {
    if (inputRef.current) {
      inputRef.current.focus()
    }
  }

  const handleClick = useCallback((event) => {
    setIsClicked(true)
    setIsLoading(true)
    event.stopPropagation()
  }, [])

  const handleHoverImageLoad = () => {
    setIsHoverImageLoaded(true)
  }

  const [isLoading, setIsLoading] = useState(true)

  return (
    <Box as="label" cursor="pointer" data-testid={`image-${props.value}`}>
      <Center
        {...getCheckboxProps()}
        border={3}
        borderStyle="solid"
        borderColor="grey"
        width={['200px', '350px']}
        height={['190px', '320px']}
        padding={2}
        fontWeight="bold"
        fontSize="xs"
        borderRadius="xl"
        whiteSpace="normal"
        margin={2}
        backgroundColor="white"
        flexDirection="column"
        textAlign="center"
        aria-label={`button-${props.label}`}
        _hover={{
          borderColor: 'brand',
        }}
        _checked={{
          borderColor: 'brand',
        }}
        _focus={{
          borderColor: 'brand',
          boxShadow: '0 0 0 2px #5bce4c',
        }}
        tabIndex={0}
        onFocus={handleFocus}
        onClick={(event) => handleClick(event)}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <RadioButton {...inputProps} ref={inputRef} />
        <Box display="flex" alignItems="center" justifyContent="center" width="90%" height="100%">
          {isLoading && <Spinner color={'black'} />}
          {isHovered ? (
            <Image
              src={imageSelected}
              onLoad={() => {
                if (isLoading) setIsLoading(false)
                handleHoverImageLoad()
              }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              width={isLoading ? '0%' : '70%'}
              height={isLoading ? '0%' : '90%'}
              borderRadius="100%"
              className={isHoverImageLoaded ? 'visible' : 'hidden'}
            />
          ) : (
            // <Image src={imageSelected} onLoad={handleHoverImageLoad} className={isHoverImageLoaded ? 'visible' : 'hidden'} />
            <Image
              src={image}
              onLoad={() => {
                if (isLoading) setIsLoading(false)
                handleHoverImageLoad()
              }}
              display="flex"
              flexDirection="column"
              justifyContent="center"
              alignItems="center"
              width={isLoading ? '0%' : '70%'}
              height={isLoading ? '0%' : '90%'}
              borderRadius="100%"
              className={!isHovered ? 'visible' : 'hidden'}
            />
          )}
        </Box>
        {!isLoading && (
          <Flex flex="40%" display="flex" flexDirection="column">
            <Text fontSize={['12px', '16px']} fontWeight="bolder" color={'black'}>
              {props.label}
            </Text>
            <Text fontSize={['10px', '14px']} fontWeight="100" color={'black'}>
              {props.secondaryLabel}
            </Text>
          </Flex>
        )}
      </Center>
    </Box>
  )
}
